import {
  ChartBarIcon,
  ClipboardDocumentCheckIcon,
  TableCellsIcon,
  BanknotesIcon,
  CalendarDaysIcon,
  LightBulbIcon,
  BriefcaseIcon,
  ArchiveBoxIcon,
  UsersIcon,
  UserGroupIcon,
  Squares2X2Icon,
  ReceiptPercentIcon,
  CreditCardIcon,
} from "@heroicons/react/20/solid";
import ReporteResumen from "./ReporteResumen";
import ReporteRentabilidad from "./ReporteRentabilidad";
import ReporteEstadoResultados from "./ReporteEstadoResultados";
import ReporteAgendaVendida from "./ReporteAgendaVendida";
import ReporteFlujoCaja from "./ReporteFlujoCaja";
import ReporteMetodosPago from "./ReporteMetodosPago";
import ReporteEstacionalidad from "./ReporteEstacionalidad";
import ReporteComercial from "./ReporteComercial";
import ReporteProveedores from "./ReporteProveedores";
import ReporteInventario from "./ReporteInventario";
import ReporteClientes from "./ReporteClientes";
import ReporteCartera from "./ReporteCartera";
import ReporteEstructuraCostos from "./ReporteEstructuraCostos";
import ReportePlanners from "./ReportePlanners";
import ReporteOcupacion from "./ReporteOcupacion";
import ReporteUsoVenue from "./ReporteUsoVenue";

// El catálogo de reportes. Cada uno es un renglón del índice lateral y una
// pantalla propia con sus controles: `controles: "periodo"` usa el selector
// compartido (presets + rango de fechas); "propios" significa que el reporte
// trae los suyos (el estado de resultados se navega por año, la cartera es
// una foto de hoy y no se acota).
//
// `grupo` ordena el índice. Todos son de venue: los de plataforma viven en
// registryAdmin.js, en su propia pantalla.
export const GRUPOS = [
  { id: "resultados", label: "Resultados" },
  { id: "dinero", label: "Dinero" },
  { id: "comercial", label: "Comercial" },
  { id: "operacion", label: "Operación" },
];

export const REPORTES = [
  {
    id: "resumen",
    grupo: "resultados",
    titulo: "Resumen general",
    descripcion:
      "El vistazo completo del período: valor, utilidad, caja, eventos y leads, con la tendencia de 12 meses.",
    Icon: ChartBarIcon,
    controles: "periodo",
    Component: ReporteResumen,
  },
  {
    id: "rentabilidad",
    grupo: "resultados",
    titulo: "Rentabilidad de eventos",
    descripcion:
      "Evento por evento: cuánto valió, cuánto costó y cuánto dejó, en orden cronológico.",
    Icon: ClipboardDocumentCheckIcon,
    controles: "periodo",
    Component: ReporteRentabilidad,
  },
  {
    id: "estado-resultados",
    grupo: "resultados",
    titulo: "Estado de resultados",
    descripcion:
      "El año mes por mes leído por eventos: valor, costos y utilidad del mes en que ocurren, se cobren cuando se cobren.",
    Icon: TableCellsIcon,
    controles: "propios",
    Component: ReporteEstadoResultados,
  },
  {
    id: "agenda-vendida",
    grupo: "resultados",
    titulo: "Agenda vendida",
    descripcion:
      "Lo que ya vendiste y todavía no ocurre, mes por mes hacia adelante: cuánto vale, cuánto ya cobraste y hasta cuándo llega tu agenda.",
    Icon: CalendarDaysIcon,
    // Mira hacia adelante siempre, desde hoy: acotarlo a un período sería
    // preguntarle al backlog por el pasado.
    controles: "propios",
    Component: ReporteAgendaVendida,
  },
  {
    id: "estacionalidad",
    grupo: "resultados",
    titulo: "Estacionalidad",
    descripcion:
      "Qué meses del año te dejan más: la utilidad de los eventos atribuida al mes en que ocurren, sumando todos los años.",
    Icon: CalendarDaysIcon,
    controles: "propios",
    Component: ReporteEstacionalidad,
  },
  {
    id: "flujo-caja",
    grupo: "dinero",
    titulo: "Flujo de caja",
    descripcion:
      "El dinero que entró y salió cada mes: ingresos, reembolsos, egresos y neto, venga del evento que venga.",
    Icon: BanknotesIcon,
    controles: "propios",
    Component: ReporteFlujoCaja,
  },
  {
    id: "metodos-pago",
    grupo: "dinero",
    titulo: "Métodos de pago",
    descripcion:
      "El dinero del período por cómo se movió: cuánto entró y salió por transferencia, efectivo, tarjeta y cada método que uses.",
    Icon: CreditCardIcon,
    controles: "periodo",
    Component: ReporteMetodosPago,
  },
  {
    id: "cartera",
    grupo: "dinero",
    titulo: "Cartera y cobranza",
    descripcion:
      "La antigüedad de tus saldos: lo que te deben y lo que debes, repartido por vencimiento. Es una foto de hoy, no del período.",
    Icon: ReceiptPercentIcon,
    controles: "propios",
    Component: ReporteCartera,
  },
  {
    id: "estructura-costos",
    grupo: "dinero",
    titulo: "Estructura de costos",
    descripcion:
      "Cuánto de tu gasto es fijo y cuánto depende de vender, por categoría y mes a mes.",
    Icon: Squares2X2Icon,
    controles: "periodo",
    Component: ReporteEstructuraCostos,
  },
  {
    id: "comercial",
    grupo: "comercial",
    titulo: "Embudo comercial",
    descripcion:
      "Cuántos leads llegan, de dónde vienen y cuántos se cierran, con su tendencia mensual.",
    Icon: LightBulbIcon,
    controles: "periodo",
    Component: ReporteComercial,
  },
  {
    id: "clientes",
    grupo: "comercial",
    titulo: "Clientes",
    descripcion:
      "Quién te deja más, quién repite y cuánto te deben: el directorio de clientes leído como negocio.",
    Icon: UsersIcon,
    controles: "periodo",
    Component: ReporteClientes,
  },
  {
    id: "planners",
    grupo: "comercial",
    titulo: "Planners y comisiones",
    descripcion:
      "Cuánto negocio te trae cada planner y a qué costo, con las comisiones mes a mes.",
    Icon: UserGroupIcon,
    controles: "periodo",
    Component: ReportePlanners,
  },
  {
    id: "ocupacion",
    grupo: "operacion",
    titulo: "Ocupación y espacios",
    descripcion:
      "Qué tan lleno estuviste, qué días de la semana concentran tus eventos y cuál de tus espacios sostiene el negocio.",
    Icon: CalendarDaysIcon,
    controles: "periodo",
    Component: ReporteOcupacion,
  },
  {
    id: "proveedores-servicios",
    grupo: "operacion",
    titulo: "Proveedores y servicios",
    descripcion:
      "A quién le pagas y cuánto, y qué servicios que compraste ya se pagaron solos.",
    Icon: BriefcaseIcon,
    controles: "periodo",
    Component: ReporteProveedores,
  },
  {
    id: "inventario",
    grupo: "operacion",
    titulo: "Piezas y equipo",
    descripcion:
      "Cuántas piezas tienes, en qué estado, cuánto se usaron y qué han costado.",
    Icon: ArchiveBoxIcon,
    controles: "periodo",
    Component: ReporteInventario,
  },
  {
    id: "uso-panel",
    grupo: "operacion",
    titulo: "Uso del panel",
    descripcion:
      "Quién de tu equipo entra al panel, en qué secciones trabaja y cuándo lo hizo por última vez. Solo de este venue.",
    Icon: UsersIcon,
    controles: "propios",
    Component: ReporteUsoVenue,
    // Se arma sobre la bitácora del venue, que el inversionista no lee
    // (P-FRA-13): para él saldría siempre a medias.
    requiereBitacora: true,
  },
];

export const reportePorId = (id) => REPORTES.find((r) => r.id === id) || null;

// Grupos con sus reportes; los vacíos no se pintan.
export const GRUPOS_CON_REPORTES = GRUPOS.map((g) => ({
  ...g,
  reportes: REPORTES.filter((r) => r.grupo === g.id),
})).filter((g) => g.reportes.length > 0);

// Lo mismo, sin los reportes que dependen de la bitácora (inversionista).
export const gruposConReportes = ({ sinBitacora = false } = {}) =>
  sinBitacora
    ? GRUPOS_CON_REPORTES.map((g) => ({
        ...g,
        reportes: g.reportes.filter((r) => !r.requiereBitacora),
      })).filter((g) => g.reportes.length > 0)
    : GRUPOS_CON_REPORTES;
